import type { ColumnDef } from "@tanstack/react-table";
import { format, differenceInDays, differenceInHours } from "date-fns";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AlertTriangle, GitFork, FileText } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { SortIcon } from "@/components/common/SortIcon/SortIcon";
import { formatTime } from "@/lib/utils/date";
import type { Task } from "../types";
import { getTaskStatusColor } from "../statusStyles";
import {
  TaskActionsCell,
  type ActiveActivity,
  type UserInfo,
} from "./TaskActionsCell";
import { AdjustmentRowActions } from "./AdjustmentRowActions";

interface TaskColumnsProps {
  onEdit?: ((task: Task) => void) | undefined;
  onDelete?: ((task: Task) => void) | undefined;
  onStartActivity?: (() => void) | undefined;
  onStopActivity?: (() => void) | undefined;
  onActionSuccess?: (() => void) | undefined;
  onToggleExpand?: ((taskId: number) => void) | undefined;
  expandedTasks?: Record<number, boolean> | undefined;
  loadingTasks?: Record<number, boolean> | undefined;
  hasAccess?: ((resource: string, action: string) => boolean) | undefined;
  orderBy?: string | undefined;
  order?: "asc" | "desc" | undefined;
  onSort?: ((column: string) => void) | undefined;
  activeActivity?: ActiveActivity | null | undefined;
  userInfo?: UserInfo | null | undefined;
}

export function useNewTaskColumns({
  onEdit,
  onDelete,
  onStartActivity,
  onStopActivity,
  onActionSuccess,
  onToggleExpand,
  expandedTasks = {},
  loadingTasks = {},
  hasAccess = () => true,
  orderBy,
  order,
  onSort,
  activeActivity,
  userInfo,
}: TaskColumnsProps = {}): ColumnDef<Task>[] {
  const { t } = useTranslation();

  const columns: ColumnDef<Task>[] = [];

  // 'id' column (first column)
  columns.push({
    accessorKey: "id",
    header: () => (
      <div
        className="flex items-center justify-center"
        role="button"
        tabIndex={0}
        onClick={() => onSort?.("id")}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && onSort?.("id")
        }
      >
        <p className="font-bold text-center">{t("id")}</p>
      </div>
    ),
    cell: ({ row }) => {
      const id = row.original.id;
      return (
        <p className="font-bold text-center">{id}</p>
      );
    },
  });

  // Actions column (second column)
  columns.push({
    id: "actions",
    header: () => <p className="font-bold text-center">{t("actionsLabel")}</p>,
    cell: ({ row }) => {
      const task = row.original;

      // Adjustment rows get their own action set (Start/Stop + Finish/Edit/Delete)
      // — never the original-task review/accept actions.
      if (task.isAdjustment) {
        return (
          <AdjustmentRowActions
            task={task}
            activeActivity={activeActivity}
            userInfo={userInfo}
            hasAccess={hasAccess}
            expandedTasks={expandedTasks}
            loadingTasks={loadingTasks}
            onToggleExpand={onToggleExpand}
            onStartActivity={onStartActivity}
            onStopActivity={onStopActivity}
            onActionSuccess={onActionSuccess}
          />
        );
      }

      return (
        <TaskActionsCell
          task={task}
          activeActivity={activeActivity}
          userInfo={userInfo}
          hasAccess={hasAccess}
          expandedTasks={expandedTasks}
          loadingTasks={loadingTasks}
          onToggleExpand={onToggleExpand}
          onEdit={onEdit}
          onDelete={onDelete}
          onStartActivity={onStartActivity}
          onStopActivity={onStopActivity}
          onActionSuccess={onActionSuccess}
        />
      );
    },
  });

  // Status column — sorts by backend newStatus field
  columns.push({
    id: "status",
    accessorKey: "newStatus",
    header: () => (
      <div
        className="flex items-center cursor-pointer"
        role="button"
        tabIndex={0}
        onClick={() => onSort?.("newStatus")}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && onSort?.("newStatus")
        }
      >
        <p className="font-bold">{t("status.status")}</p>
        <SortIcon active={orderBy === "newStatus"} direction={order} />
      </div>
    ),
    cell: ({ row }) => {
      const status = row.original.newStatus;
      return (
        <Badge className={`rounded-full ${getTaskStatusColor(status)}`}>
          {t(`newTaskStatus.${status}`)}
        </Badge>
      );
    },
  });

  // Run Time column (calculated from activities)
  columns.push({
    accessorKey: "runTime",
    header: () => <p className="font-bold">{t("runTime")}</p>,
    cell: ({ row }) => {
      const activities = row.original.activities || [];
      const activityTime = activities.reduce(
        (total, activity) => total + (activity.userEnteredTimeInSeconds || 0),
        0,
      );
      return formatTime(activityTime);
    },
  });

  // Assigned To column
  columns.push({
    accessorKey: "assignedTo",
    header: () => <p className="font-bold">{t("assignedTo")}</p>,
    cell: ({ row }) => (
      <div>
        <Link
          to={`/app/users/${row.original?.assignedStaff?.id}`}
          className="text-blue-700 hover:underline"
        >
          {row.original?.assignedStaff?.name || "-"}
        </Link>
      </div>
    ),
  });

  // Result column (work result/summary recorded when sending to review)
  columns.push({
    accessorKey: "result",
    header: () => <p className="font-bold">{t("taskResult")}</p>,
    cell: ({ row }) => {
      const result = row.original?.result;
      if (!result) return "-";
      return (
        <div className="max-w-[16rem] truncate" title={result}>
          {result}
        </div>
      );
    },
  });

  // Description column
  columns.push({
    accessorKey: "description",
    header: () => (
      <div
        className="flex items-center cursor-pointer"
        role="button"
        tabIndex={0}
        onClick={() => onSort?.("description")}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && onSort?.("description")
        }
      >
        <p className="font-bold">{t("description")}</p>
        <SortIcon active={orderBy === "description"} direction={order} />
      </div>
    ),
    cell: ({ row }) => (
      <div className="flex flex-col items-start gap-1.5 w-[30rem] max-w-[30rem]">
        {row.original.isAdjustment ? (
          <Badge
            variant="outline"
            className="w-fit gap-1 border-blue-300 bg-blue-50 text-blue-700"
          >
            <GitFork className="h-3 w-3" />
            {t("adjustment.adjustmentTag")}
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="w-fit gap-1 border-slate-300 bg-slate-50 text-slate-600"
          >
            <FileText className="h-3 w-3" />
            {t("adjustment.original")}
          </Badge>
        )}
        <Link
          to={`/app/new-tasks/${row.original.id}`}
          className="w-full text-blue-700 hover:underline !whitespace-normal break-words"
        >
          {row.original.description || "-"}
        </Link>
      </div>
    ),
  });

  // Comment column (only shows value for rejected statuses)
  columns.push({
    accessorKey: "comment",
    header: () => <p className="font-bold">{t("comment")}</p>,
    cell: ({ row }) => {
      const { newStatus, comment } = row.original;
      if (newStatus !== "LEAD_REJECT" && newStatus !== "PARTNER_REJECT")
        return "-";
      return comment || "-";
    },
  });

  // Due At column
  columns.push({
    accessorKey: "dueAt",
    header: () => (
      <div
        className="flex items-center cursor-pointer"
        role="button"
        tabIndex={0}
        onClick={() => onSort?.("dueAt")}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && onSort?.("dueAt")
        }
      >
        <p className="font-bold">{t("taskDueAt")}</p>
        <SortIcon active={orderBy === "dueAt"} direction={order} />
      </div>
    ),
    cell: ({ row }) => (
      <div>
        {row.original.dueAt
          ? format(new Date(row.original.dueAt), "yyyy-MM-dd")
          : "-"}
      </div>
    ),
  });

  // Created At column
  columns.push({
    accessorKey: "createdAt",
    header: () => (
      <div
        className="flex items-center cursor-pointer"
        role="button"
        tabIndex={0}
        onClick={() => onSort?.("createdAt")}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && onSort?.("createdAt")
        }
      >
        <p className="font-bold">{t("createdAt")}</p>
        <SortIcon active={orderBy === "createdAt"} direction={order} />
      </div>
    ),
    cell: ({ row }) => (
      <div>
        {row.original.createdAt
          ? format(new Date(row.original.createdAt), "yyyy-MM-dd")
          : "-"}
      </div>
    ),
  });

  // Closed At column (endedAt — set when the task chain is finished/billed)
  columns.push({
    accessorKey: "endedAt",
    header: () => (
      <div
        className="flex items-center cursor-pointer"
        role="button"
        tabIndex={0}
        onClick={() => onSort?.("endedAt")}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && onSort?.("endedAt")
        }
      >
        <p className="font-bold">{t("taskClosedAt")}</p>
        <SortIcon active={orderBy === "endedAt"} direction={order} />
      </div>
    ),
    cell: ({ row }) => (
      <div>
        {row.original.endedAt
          ? format(new Date(row.original.endedAt), "yyyy-MM-dd")
          : "-"}
      </div>
    ),
  });

  // Matter column
  columns.push({
    accessorKey: "matter",
    header: () => <p className="font-bold">{t("matter")}</p>,
    cell: ({ row }) => (
      <div>
        <Link
          to={`/app/matters/${row.original?.matter?.id}`}
          className="text-blue-700 hover:underline"
        >
          {row.original?.matter?.name || "-"}
        </Link>
      </div>
    ),
  });

  // Contract column
  columns.push({
    accessorKey: "contract",
    header: () => <p className="font-bold">{t("contract")}</p>,
    cell: ({ row }) => (
      <div>
        <Link
          to={`/app/contracts/${row.original?.matter?.contract?.id}`}
          className="text-blue-700 hover:underline"
        >
          {row.original?.matter?.contract?.name || "-"}
        </Link>
      </div>
    ),
  });

  // Client column
  columns.push({
    accessorKey: "client",
    header: () => <p className="font-bold">{t("client")}</p>,
    cell: ({ row }) => {
      const client = row.original?.matter?.contract?.client;
      const fullName = client
        ? `${client.lastname ?? "-"} ${client.name ?? "-"} ${client.middlename ?? "-"}`
        : "-";
      return (
        <div>
          <Link
            to={`/app/clients/${client?.id}`}
            className="text-blue-700 hover:underline"
          >
            {fullName}
          </Link>
        </div>
      );
    },
  });

  // Assigned By column
  columns.push({
    accessorKey: "assignedBy",
    header: () => <p className="font-bold">{t("assignedBy")}</p>,
    cell: ({ row }) => (
      <div>
        <Link
          to={`/app/users/${row.original?.createdBy?.id}`}
          className="text-blue-700 hover:underline"
        >
          {row.original?.createdBy?.name || "-"}
        </Link>
      </div>
    ),
  });

  // Task Type column
  columns.push({
    accessorKey: "type",
    header: () => <p className="font-bold">{t("taskType")}</p>,
    cell: ({ row }) => (
      <div>
        <Link
          to={`/app/task-types/${row.original?.type?.id}`}
          className="text-blue-700 hover:underline"
        >
          {row.original?.type?.name || "-"}
        </Link>
      </div>
    ),
  });

  // Time Estimate column
  columns.push({
    accessorKey: "timeEstimate",
    header: () => <p className="font-bold">{t("timeEstimate")}</p>,
    cell: ({ row }) => {
      const dueAt = row.original.dueAt;
      if (!dueAt) return "-";

      const now = new Date();
      const dueDate = new Date(dueAt);
      const daysLeft = differenceInDays(dueDate, now);

      if (daysLeft < 1 && daysLeft >= 0) {
        const hoursLeft = differenceInHours(dueDate, now);
        if (hoursLeft < 0) {
          return (
            <div>
              <span className="ml-1 text-sm text-muted-foreground">
                ({Math.abs(hoursLeft)} {t("hoursAgo")})
              </span>
            </div>
          );
        }
        return (
          <div>
            <span className="ml-1 text-sm text-muted-foreground">
              ({hoursLeft} {t("hoursLeft")})
            </span>
          </div>
        );
      } else if (daysLeft < 0) {
        return (
          <div>
            <span className="ml-1 text-sm text-muted-foreground">
              ({Math.abs(daysLeft)} {t("daysAgo")})
            </span>
          </div>
        );
      } else {
        return (
          <div>
            <span className="ml-1 text-sm text-muted-foreground">
              ({daysLeft} {t("daysLeft")})
            </span>
          </div>
        );
      }
    },
  });

  // Escalated column
  columns.push({
    accessorKey: "isEscalated",
    header: () => <p className="font-bold">{t("escalated")}</p>,
    cell: ({ row }) => {
      if (!row.original.isEscalated) return "-";
      return (
        <Badge className="rounded-full bg-red-100 text-red-700 hover:bg-red-100 border-red-200">
          <AlertTriangle className="h-3 w-3 mr-1" />
          {t("escalated")}
        </Badge>
      );
    },
  });

  // Billable Duration column
  columns.push({
    accessorKey: "billableDurationInSeconds",
    header: () => <p className="font-bold">{t("billableDurationInSeconds")}</p>,
    cell: ({ row }) => {
      return row.original.billableDurationInSeconds
        ? formatTime(row.original.billableDurationInSeconds)
        : "-";
    },
  });

  // Duration column
  columns.push({
    accessorKey: "durationInSeconds",
    header: () => <p className="font-bold">{t("durationInSeconds")}</p>,
    cell: ({ row }) => {
      return row.original.durationInSeconds
        ? formatTime(row.original.durationInSeconds)
        : "-";
    },
  });

  return columns;
}
